// Mirrors the `.All` vocabulary arrays published by
// backend-shared/Pay.Roadrunner.Models/Constants/PayTypesConstants.cs, plus fields
// (`taxIndicator`, `stpPaymentType`, `percentageType`) whose values are confirmed from other
// backend sources (SeedData / a bare constants class / the PRD schema line itself) even though the
// backend doesn't publish an `.All` array for them — see PRR-178. Every other enum-like string
// field (e.g. `protectedEarningType`, `superPayElementType`, `payrollTaxCategory`, and the
// remaining 8 STP code fields) has no confirmed backend vocabulary and stays free-text in the form
// layer, per PRR-126.

// PRR-150: each dropdown's raw backend `value` is paired with a human-readable `label` in one
// place, e.g. `per_meal` / "Per Meal". `SelectField` reads `value` (sent to the API, unchanged)
// and `label` (shown to the user) straight off these entries — no separate lookup map.

export const PAY_TYPE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const;

export const PAY_COMPONENT_TYPE_OPTIONS = [
  { value: 'element', label: 'Element' },
  { value: 'allowance', label: 'Allowance' },
  { value: 'deduction', label: 'Deduction' },
  { value: 'special_calculation', label: 'Special Calculation' },
  { value: 'employer_super', label: 'Employer Super' },
  { value: 'paygw', label: 'PAYGW' },
  { value: 'net_pay', label: 'Net Pay' },
  { value: 'redundancy', label: 'Redundancy' },
  { value: 'etp', label: 'ETP' },
] as const;

export type PayTypeTagTone = 'blue' | 'caution' | 'critical' | 'highlight' | 'positive' | 'orange' | 'teal' | 'pink' | 'neutral';

const PAY_COMPONENT_TAG_TONES: Record<string, PayTypeTagTone> = {
  element: 'blue',
  earning: 'blue',
  earnings: 'blue',
  allowance: 'caution',
  allowances: 'caution',
  deduction: 'critical',
  deductions: 'critical',
  special_calculation: 'highlight',
  employer_super: 'positive',
  superannuation: 'positive',
  paygw: 'orange',
  tax_withholding: 'orange',
  leave: 'blue',
  reimbursement: 'neutral',
  reimbursements: 'neutral',
  net_pay: 'teal',
  redundancy: 'pink',
  etp: 'neutral',
};

/** Shared Admin/tenant presentation contract for Pay Type component tags. */
export const payComponentTagPresentation = (value: string): { label: string; tone: PayTypeTagTone } => {
  const key = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return {
    label: PAY_COMPONENT_TYPE_OPTIONS.find((option) => option.value === key)?.label ?? value,
    tone: PAY_COMPONENT_TAG_TONES[key] ?? 'neutral',
  };
};

/** Shared Admin/tenant presentation contract for Pay Type status tags. */
export const payTypeStatusTagPresentation = (value: string): { label: string; tone: PayTypeTagTone } => {
  switch (value.toLowerCase()) {
    case 'active': return { label: 'Active', tone: 'positive' };
    case 'inactive': return { label: 'Inactive', tone: 'neutral' };
    case 'future': return { label: 'Future', tone: 'highlight' };
    case 'retired': return { label: 'Retired', tone: 'neutral' };
    case 'deactivated': return { label: 'Deactivated', tone: 'neutral' };
    case 'expired': return { label: 'Expired', tone: 'neutral' };
    default: return { label: value, tone: 'neutral' };
  }
};

export const CALCULATION_METHOD_OPTIONS = [
  { value: 'factor_of_rate', label: 'Factor of Rate' },
  { value: 'variable_amount', label: 'Variable Amount' },
  { value: 'amount_by_frequency', label: 'Amount by Frequency' },
  { value: 'amount_by_unit', label: 'Amount by Unit' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'rate_type_lookup', label: 'Rate Type Lookup' },
] as const;

export const FIXED_AMOUNT_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'per_shift', label: 'Per Shift' },
  { value: 'per_meal', label: 'Per Meal' },
  { value: 'per_km', label: 'Per KM' },
  { value: 'per_engagement', label: 'Per Engagement' },
] as const;

export const DEDUCTION_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'four_weekly', label: 'Four Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
] as const;

// PRR-178: values confirmed against admin-api/Pay.Roadrunner.AdminBL/SeedData/PayTypeCatalogueSeedData.cs's
// internal `TaxIndicators` class — not published as an `.All` array on the backend, but a fixed
// vocabulary in practice.
export const TAX_INDICATOR_OPTIONS = [
  { value: 'before_tax', label: 'Before Tax' },
  { value: 'after_tax', label: 'After Tax' },
  { value: 'extra_tax', label: 'Extra Tax' },
  { value: 'tax_averaged', label: 'Tax Averaged' },
] as const;

// PRR-178: values confirmed against PayTypesConstants.cs's `StpPaymentType` class (a bare
// constants class, no `.All` array). Labels are UI-chosen (no backend-published label mapping).
export const STP_PAYMENT_TYPE_OPTIONS = [
  { value: 'allowance', label: 'Allowance' },
  { value: 'paid_leave', label: 'Paid Leave' },
  { value: 'salary_sacrifice', label: 'Salary Sacrifice' },
  { value: 'lump_sum', label: 'Lump Sum' },
  { value: 'non_reportable', label: 'Non Reportable' },
] as const;

export const SUPER_PAY_ELEMENT_TYPE_OPTIONS = [
  { value: 'superannuation_guarantee', label: 'Superannuation Guarantee' },
  { value: 'salary_sacrifice', label: 'Salary Sacrifice' },
  { value: 'employer_extra', label: 'Employer Extra' },
  { value: 'personal_contribution_after_tax', label: 'Personal Contribution After Tax' },
  { value: 'employer_additional_supported_employment', label: 'Employer Additional Supported Employment' },
  { value: 'defined_benefit', label: 'Defined Benefit' },
] as const;

// PRR-178: values confirmed directly against the PRD schema line itself (Confluence "PRD — Pay
// Types Master", page 2895970353, §9.1): `PercentageType varchar(30) NULL, -- pay_type_list |
// fte_annual_rate (Special Calc only)`. Only relevant when CalculationMethod = percentage.
export const PERCENTAGE_TYPE_OPTIONS = [
  { value: 'pay_type_list', label: 'Pay Type List' },
  { value: 'fte_annual_rate', label: 'FTE Annual Rate' },
] as const;
